import React, { useState, useEffect, useRef } from "react";
import {
  Play,
  Pause,
  RotateCcw,
  CheckCircle2,
  FileSpreadsheet,
  CreditCard,
  ShieldCheck,
  Download,
  Check,
  Lock,
  Volume2,
  ChevronRight,
  Sparkles
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { toast } from "sonner";
import { jsPDF } from "jspdf";

export interface DemoStep {
  id: number;
  title: string;
  tag: string;
  duration: number; // segundos en auto-play
  voiceover: string;
}

const DEMO_STEPS: DemoStep[] = [
  {
    id: 1,
    title: "Carga Masiva desde Excel",
    tag: "Paso 01: Ingestión Universal",
    duration: 8,
    voiceover:
      "El administrador exporta la planilla de cartera desde su software contable habitual (Daytona, SISCO o Siigo) y la sube en 2 segundos. El motor clasifica automáticamente las unidades por mora y capital."
  },
  {
    id: 2,
    title: "Semáforo Legal & Despacho",
    tag: "Paso 02: Blindaje Ley 2300",
    duration: 8,
    voiceover:
      "La plataforma verifica en tiempo real la ventana legal de contacto (L-V 7am-7pm). Se liquidan intereses de mora a la tasa oficial de la Superfinanciera y se encolan las notificaciones institucionales."
  },
  {
    id: 3,
    title: "Notificación WhatsApp & PSE",
    tag: "Paso 03: Experiencia del Copropietario",
    duration: 10,
    voiceover:
      "El copropietario recibe en su celular un requerimiento formal con el logo del conjunto. Al tocar el botón PSE paga en 30 segundos directo a la cuenta bancaria de la copropiedad."
  },
  {
    id: 4,
    title: "Título Ejecutivo en PDF en 1 Clic",
    tag: "Paso 04: Vía Judicial Ley 675",
    duration: 9,
    voiceover:
      "Para quienes no cancelen de forma voluntaria, el sistema emite al instante el Certificado de Deuda (Art. 48 Ley 675) con mérito ejecutivo y firma digital, listo para radicar demanda de embargo."
  }
];

export default function CarteraPhInteractiveDemo() {
  const [currentStep, setCurrentStep] = useState<number>(1);
  const [isPlaying, setIsPlaying] = useState<boolean>(true);
  const [stepProgress, setStepProgress] = useState<number>(0);
  const [playbackSpeed, setPlaybackSpeed] = useState<number>(1);

  // Estados interactivos simulados
  const [isUploaded, setIsUploaded] = useState<boolean>(true);
  const [hasPaidPse, setHasPaidPse] = useState<boolean>(false);
  const [isGeneratingPdf, setIsGeneratingPdf] = useState<boolean>(false);

  const timerRef = useRef<NodeJS.Timeout | null>(null);

  const activeStep = DEMO_STEPS.find((s) => s.id === currentStep) || DEMO_STEPS[0];

  // Auto-play timer
  useEffect(() => {
    if (!isPlaying) {
      if (timerRef.current) clearInterval(timerRef.current);
      return;
    }

    const intervalMs = 100;
    const totalTicks = (activeStep.duration * 1000) / intervalMs / playbackSpeed;
    const increment = 100 / totalTicks;

    timerRef.current = setInterval(() => {
      setStepProgress((prev) => {
        if (prev >= 100) {
          setCurrentStep((curr) => {
            if (curr >= DEMO_STEPS.length) {
              return 1; // loop
            }
            return curr + 1;
          });
          return 0;
        }
        return prev + increment;
      });
    }, intervalMs);

    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [isPlaying, currentStep, activeStep.duration, playbackSpeed]);

  const handleSelectStep = (stepId: number) => {
    setCurrentStep(stepId);
    setStepProgress(0);
  };

  const handleTogglePlay = () => {
    setIsPlaying(!isPlaying);
  };

  const handleReset = () => {
    setCurrentStep(1);
    setStepProgress(0);
    setHasPaidPse(false);
    setIsPlaying(true);
  };

  const handleSimulatePayment = () => {
    setHasPaidPse(true);
    toast.success("¡Pago aprobado por $1.150.000 COP vía PSE!", {
      description: "La cuenta del Conjunto Torres del Parque recibió los fondos y la unidad quedó en Paz y Salvo."
    });
  };

  const handleDownloadSampleTitle = () => {
    try {
      setIsGeneratingPdf(true);
      const doc = new jsPDF();
      doc.setFont("helvetica", "bold");
      doc.setFontSize(14);
      doc.text("CERTIFICADO DE DEUDA - TÍTULO EJECUTIVO", 105, 25, { align: "center" });
      doc.setFontSize(10);
      doc.setFont("helvetica", "normal");
      doc.text("Artículo 48 Ley 675 de 2001 - Régimen de Propiedad Horizontal", 105, 32, { align: "center" });
      doc.line(20, 36, 190, 36);

      doc.setFont("helvetica", "bold");
      doc.text("COPROPIEDAD: CONJUNTO RESIDENCIAL TORRES DEL PARQUE P.H.", 20, 48);
      doc.text("NIT: 900.542.118-4 | ADMINISTRADOR: MiA LegalTech Partner", 20, 54);
      doc.text("DEUDOR / PROPIETARIO: JUAN CARLOS PÉREZ | APTO: 402 - TORRE 2", 20, 60);

      doc.setFont("helvetica", "normal");
      doc.text(
        "El Administrador del Conjunto certifica que el inmueble registra un saldo en mora por concepto de expensas comunes ordinarias y extraordinarias por valor de $1.150.000 COP, más intereses de mora liquidados a la tasa de usura certificada por la Superintendencia Financiera de Colombia.",
        20,
        70,
        { maxWidth: 170 }
      );

      doc.text("Total Capital: $980.000 COP", 20, 95);
      doc.text("Intereses Moratorios SFC: $170.000 COP", 20, 101);
      doc.setFont("helvetica", "bold");
      doc.text("TOTAL DEUDA EXIGIBLE CON MÉRITO EJECUTIVO: $1.150.000 COP", 20, 109);

      doc.setFont("helvetica", "normal");
      doc.text("Este documento presta mérito ejecutivo pleno ante los jueces civiles de la República.", 20, 120);
      doc.text("Firma Digital Certificada conforme a la Ley 527 de 1999.", 20, 140);
      doc.text("__________________________________________", 20, 160);
      doc.text("ADMINISTRADOR Y REPRESENTANTE LEGAL P.H.", 20, 166);

      doc.save("Titulo_Ejecutivo_Art48_Apto402.pdf");
      toast.success("Título Ejecutivo Art. 48 descargado con éxito.");
    } catch (e) {
      console.error(e);
      toast.error("Error al generar el documento.");
    } finally {
      setIsGeneratingPdf(false);
    }
  };

  return (
    <div className="rounded-2xl border border-slate-200 bg-white text-slate-800 shadow-md overflow-hidden max-w-5xl mx-auto">
      {/* ── BARRA SUPERIOR DE CONTROL SOBRIA Y CLARA ── */}
      <div className="bg-slate-50 border-b border-slate-200 px-4 md:px-6 py-3 flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2.5">
          <div className="flex items-center justify-center h-8 w-8 rounded-lg bg-emerald-50 border border-emerald-200 text-emerald-700">
            <Sparkles className="h-4 w-4" />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <span className="font-bold text-sm md:text-base text-slate-900 tracking-tight">
                Simulador de Cartera PH
              </span>
              <Badge variant="outline" className="border-slate-300 text-slate-700 bg-white text-[10px] font-medium py-0.5">
                Capítulo {currentStep} de 4
              </Badge>
            </div>
            <p className="text-xs text-slate-500 hidden sm:block">
              Flujo paso a paso bajo Ley 675 de 2001 y Ley 2300 de 2023
            </p>
          </div>
        </div>

        {/* Controles del Reproductor */}
        <div className="flex items-center gap-2">
          <Button
            size="sm"
            variant="outline"
            onClick={handleTogglePlay}
            className="border-slate-300 bg-white hover:bg-slate-50 text-slate-700 text-xs gap-1.5 h-8 font-medium shadow-xs"
          >
            {isPlaying ? (
              <>
                <Pause className="h-3.5 w-3.5 text-amber-600 fill-amber-600" />
                Pausar
              </>
            ) : (
              <>
                <Play className="h-3.5 w-3.5 text-emerald-600 fill-emerald-600" />
                Reproducir
              </>
            )}
          </Button>

          <Button
            size="sm"
            variant="ghost"
            onClick={handleReset}
            className="text-slate-500 hover:text-slate-800 hover:bg-slate-100 h-8 px-2"
            title="Reiniciar desde el inicio"
          >
            <RotateCcw className="h-3.5 w-3.5" />
          </Button>

          <Button
            size="sm"
            variant="ghost"
            onClick={() => setPlaybackSpeed((s) => (s === 1 ? 1.5 : s === 1.5 ? 2 : 1))}
            className="text-xs font-mono text-slate-500 hover:text-slate-800 hover:bg-slate-100 h-8 px-2"
            title="Velocidad"
          >
            {playbackSpeed}x
          </Button>
        </div>
      </div>

      {/* ── BARRA DE CAPÍTULOS TIPO PESTAÑAS ELEGANTES ── */}
      <div className="grid grid-cols-4 bg-slate-50/70 border-b border-slate-200 text-xs">
        {DEMO_STEPS.map((step) => {
          const isActive = currentStep === step.id;
          const isPassed = currentStep > step.id;
          return (
            <button
              key={step.id}
              onClick={() => handleSelectStep(step.id)}
              className={`text-left p-3 border-r last:border-r-0 border-slate-200 transition-all relative ${
                isActive
                  ? "bg-white text-slate-900 font-semibold shadow-xs"
                  : "text-slate-500 hover:text-slate-800 hover:bg-slate-100/60"
              }`}
            >
              <div className="flex items-center gap-1.5">
                <span
                  className={`flex items-center justify-center h-4 w-4 rounded-full text-[10px] font-semibold ${
                    isActive
                      ? "bg-emerald-600 text-white"
                      : isPassed
                      ? "bg-emerald-100 text-emerald-800"
                      : "bg-slate-200 text-slate-600"
                  }`}
                >
                  {isPassed ? "✓" : step.id}
                </span>
                <span className="truncate hidden md:inline text-[11px]">{step.title}</span>
                <span className="truncate md:hidden text-[11px]">Paso {step.id}</span>
              </div>

              {/* Barra de progreso de este paso */}
              <div className="w-full bg-slate-200 h-1 rounded-full mt-2 overflow-hidden">
                <div
                  className="h-full bg-emerald-600 transition-all duration-100 ease-linear"
                  style={{
                    width: isPassed ? "100%" : isActive ? `${stepProgress}%` : "0%"
                  }}
                />
              </div>
            </button>
          );
        })}
      </div>

      {/* ── ESCENARIO PRINCIPAL INTERACTIVO (CLARO & SOBRIO) ── */}
      <div className="p-4 md:p-6 min-h-[440px] flex flex-col justify-between bg-white">
        {/* PASO 1: CARGA MASIVA EXCEL */}
        {currentStep === 1 && (
          <div className="space-y-5 animate-fadeIn">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
              <div>
                <Badge variant="outline" className="border-slate-300 text-slate-700 bg-slate-50 mb-1.5 text-xs font-normal">
                  Compatible con Daytona, SISCO, Siigo, WordOffice
                </Badge>
                <h3 className="text-lg md:text-xl font-bold text-slate-900">
                  Carga Inteligente de Cartera en 2 Segundos
                </h3>
              </div>
              <Button
                size="sm"
                className="bg-emerald-600 hover:bg-emerald-700 text-white font-medium gap-1.5 shadow-xs shrink-0"
                onClick={() => {
                  setIsUploaded(false);
                  setTimeout(() => {
                    setIsUploaded(true);
                    toast.success("120 unidades importadas y categorizadas correctamente.");
                  }, 600);
                }}
              >
                <FileSpreadsheet className="h-3.5 w-3.5" />
                Simular Carga de Excel
              </Button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {/* Tarjeta de Archivo */}
              <div className="p-4 rounded-xl border border-slate-200 bg-slate-50/50 space-y-3">
                <div className="flex items-center gap-3">
                  <div className="p-2.5 rounded-lg bg-emerald-50 text-emerald-700 border border-emerald-200">
                    <FileSpreadsheet className="h-5 w-5" />
                  </div>
                  <div>
                    <div className="font-semibold text-xs text-slate-900">Cartera_Torres_del_Parque.xlsx</div>
                    <div className="text-[11px] text-slate-500">120 unidades privadas</div>
                  </div>
                </div>

                <div className="space-y-1.5 text-xs">
                  <div className="flex justify-between text-slate-600 text-[11px]">
                    <span>Procesamiento:</span>
                    <span className="text-emerald-700 font-semibold">100% Clasificado</span>
                  </div>
                  <Progress value={isUploaded ? 100 : 35} className="h-1.5 bg-slate-200" />
                </div>

                <div className="pt-2 border-t border-slate-200 text-[11px] text-slate-600 flex items-center gap-1.5">
                  <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600 shrink-0" />
                  Mapeo automático de Apto, Propietario, WhatsApp y Saldo.
                </div>
              </div>

              {/* Diagnóstico Automático en 3 Rangos */}
              <div className="md:col-span-2 p-4 rounded-xl border border-slate-200 bg-slate-50/50 space-y-3">
                <div className="flex items-center justify-between">
                  <div className="font-semibold text-xs text-slate-900">
                    Diagnóstico Automático de Cartera (Ley 675)
                  </div>
                  <Badge variant="outline" className="border-slate-300 text-slate-700 bg-white text-xs font-mono">
                    $14.280.000 COP en Riesgo
                  </Badge>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <div className="p-3 rounded-lg border border-emerald-200 bg-emerald-50/50">
                    <div className="text-[10px] font-bold text-emerald-800 uppercase">Preventivo (1-30d)</div>
                    <div className="text-base font-extrabold text-slate-900 mt-0.5">8 Unidades</div>
                    <div className="text-xs text-emerald-700 font-mono font-medium">$2.400.000 COP</div>
                  </div>
                  <div className="p-3 rounded-lg border border-amber-200 bg-amber-50/50">
                    <div className="text-[10px] font-bold text-amber-800 uppercase">Prejurídico (31-60d)</div>
                    <div className="text-base font-extrabold text-slate-900 mt-0.5">6 Unidades</div>
                    <div className="text-xs text-amber-700 font-mono font-medium">$4.980.000 COP</div>
                  </div>
                  <div className="p-3 rounded-lg border border-rose-200 bg-rose-50/50">
                    <div className="text-[10px] font-bold text-rose-800 uppercase">Demanda (&gt;60d)</div>
                    <div className="text-base font-extrabold text-slate-900 mt-0.5">4 Unidades</div>
                    <div className="text-xs text-rose-700 font-mono font-medium">$6.900.000 COP</div>
                  </div>
                </div>

                <div className="text-[11px] text-slate-600 flex items-center gap-1.5 pt-1">
                  <Lock className="h-3 w-3 text-slate-400" />
                  Calcula automáticamente los intereses legales certificados por la Superfinanciera sin hojas manuales.
                </div>
              </div>
            </div>
          </div>
        )}

        {/* PASO 2: SEMÁFORO LEGAL & DESPACHO AUTOMÁTICO */}
        {currentStep === 2 && (
          <div className="space-y-5 animate-fadeIn">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
              <div>
                <Badge variant="outline" className="border-slate-300 text-slate-700 bg-slate-50 mb-1.5 text-xs font-normal">
                  Cumplimiento Ley 2300 de 2023
                </Badge>
                <h3 className="text-lg md:text-xl font-bold text-slate-900">
                  Blindaje Algorítmico contra Sanciones de la SIC
                </h3>
              </div>
              <div className="flex items-center gap-1.5 text-xs font-semibold text-emerald-800 bg-emerald-50 px-2.5 py-1 rounded-full border border-emerald-200">
                <span className="h-2 w-2 rounded-full bg-emerald-600" />
                <span>VENTANA LEGAL HÁBIL</span>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {/* Reglas de la Ley 2300 */}
              <div className="p-4 rounded-xl border border-slate-200 bg-slate-50/50 space-y-2.5">
                <div className="font-semibold text-xs text-slate-900 flex items-center gap-2">
                  <ShieldCheck className="h-4 w-4 text-emerald-700" />
                  Validación Previa al Despacho
                </div>
                <ul className="text-xs space-y-2 text-slate-600">
                  <li className="flex items-center gap-2">
                    <Check className="h-3.5 w-3.5 text-emerald-600 shrink-0" />
                    <span>Lunes a Viernes (7:00 AM - 7:00 PM)</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <Check className="h-3.5 w-3.5 text-emerald-600 shrink-0" />
                    <span>Sábados (8:00 AM - 3:00 PM)</span>
                  </li>
                  <li className="flex items-center gap-2 text-rose-700 font-medium">
                    <span className="h-1.5 w-1.5 rounded-full bg-rose-600 shrink-0" />
                    <span>Bloqueo Dominical y Festivos Activo</span>
                  </li>
                  <li className="flex items-center gap-2 text-slate-700">
                    <Check className="h-3.5 w-3.5 text-emerald-600 shrink-0" />
                    <span>Máximo 2 contactos semanales por moroso</span>
                  </li>
                </ul>
              </div>

              {/* Bitácora de Despacho Inmutable */}
              <div className="md:col-span-2 p-4 rounded-xl border border-slate-200 bg-slate-50/50 space-y-2.5">
                <div className="flex items-center justify-between text-xs">
                  <span className="font-semibold text-slate-900">
                    Bitácora Inmutable de Notificaciones Generadas
                  </span>
                  <span className="text-slate-500 font-mono">18 / 18 Encoladas</span>
                </div>

                <div className="space-y-1.5 text-xs">
                  <div className="p-2.5 rounded-lg bg-white border border-slate-200 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span className="h-2 w-2 rounded-full bg-emerald-600" />
                      <span className="font-medium text-slate-800">Apto 402 - Juan Carlos Pérez</span>
                    </div>
                    <span className="text-emerald-700 font-medium">Entregado WhatsApp ✓✓</span>
                  </div>
                  <div className="p-2.5 rounded-lg bg-white border border-slate-200 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span className="h-2 w-2 rounded-full bg-emerald-600" />
                      <span className="font-medium text-slate-800">Apto 205 - María Elena Gómez</span>
                    </div>
                    <span className="text-emerald-700 font-medium">Entregado WhatsApp ✓✓</span>
                  </div>
                  <div className="p-2.5 rounded-lg bg-white border border-slate-200 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span className="h-2 w-2 rounded-full bg-amber-500" />
                      <span className="font-medium text-slate-800">Apto 701 - Roberto Carlos Ruiz</span>
                    </div>
                    <span className="text-amber-700 font-medium">En cola de envío (08:30 AM)</span>
                  </div>
                </div>

                <div className="text-[11px] text-slate-500 pt-2 border-t border-slate-200 flex items-center justify-between">
                  <span>Auditoría legal con sello de tiempo (Timestamp Ley 527)</span>
                  <span className="text-emerald-700 font-medium">100% Blindado ante la SIC</span>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* PASO 3: CELULAR EN VIVO & BOTÓN PSE */}
        {currentStep === 3 && (
          <div className="space-y-4 animate-fadeIn">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
              <div>
                <Badge variant="outline" className="border-slate-300 text-slate-700 bg-slate-50 mb-1 text-xs font-normal">
                  Recaudo Inmediato en 30 Segundos
                </Badge>
                <h3 className="text-lg md:text-xl font-bold text-slate-900">
                  Experiencia del Residente: Notificación con Botón PSE Oficial
                </h3>
              </div>
              <Badge variant="outline" className="border-emerald-200 text-emerald-800 bg-emerald-50 text-xs">
                Directo a la cuenta del Conjunto
              </Badge>
            </div>

            {/* Split Screen */}
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-5 items-center">
              {/* Izquierda: Explicación y Estado del Conjunto */}
              <div className="lg:col-span-6 space-y-3">
                <div className="p-4 rounded-xl bg-slate-50/60 border border-slate-200 space-y-2.5">
                  <div className="text-xs font-bold text-slate-700 uppercase tracking-wide">
                    Sin intermediarios bancarios ni comisiones
                  </div>
                  <p className="text-xs text-slate-600 leading-relaxed">
                    El residente recibe el cobro oficial en su WhatsApp personal con el desglose exacto de su mora e intereses legales. Al tocar el botón PSE, el dinero entra directo a la cuenta del conjunto sin comisiones de cobro.
                  </p>
                  <div className="grid grid-cols-2 gap-2 text-xs">
                    <div className="p-2.5 rounded-lg bg-white border border-slate-200">
                      <div className="text-slate-500 text-[11px]">Saldo del Moroso:</div>
                      <div className="text-sm font-bold text-slate-900 font-mono">$1.150.000 COP</div>
                    </div>
                    <div className="p-2.5 rounded-lg bg-white border border-slate-200">
                      <div className="text-slate-500 text-[11px]">Estado en Plataforma:</div>
                      <div className={`text-sm font-bold font-mono ${hasPaidPse ? "text-emerald-700" : "text-amber-700"}`}>
                        {hasPaidPse ? "PAZ Y SALVO ✓" : "PENDIENTE DE PAGO"}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="p-3 rounded-lg bg-emerald-50 border border-emerald-200 text-xs text-emerald-800 flex items-center justify-between">
                  <span>Pruebe el botón en el celular a la derecha ➔</span>
                  <Button
                    size="sm"
                    variant="outline"
                    className="border-emerald-300 bg-white text-emerald-800 hover:bg-emerald-100 text-xs h-7 font-medium shadow-xs"
                    onClick={() => setHasPaidPse(false)}
                  >
                    Restablecer
                  </Button>
                </div>
              </div>

              {/* Derecha: Simulación Smartphone Sobria */}
              <div className="lg:col-span-6 flex justify-center">
                <div className="w-full max-w-[310px] rounded-[34px] p-3 bg-slate-100 border-2 border-slate-300 shadow-md relative">
                  {/* Notch */}
                  <div className="w-24 h-3.5 bg-slate-300 rounded-b-lg mx-auto mb-2" />

                  {/* Pantalla WhatsApp */}
                  <div className="rounded-[22px] bg-[#f0f2f5] text-slate-800 overflow-hidden text-xs border border-slate-200 flex flex-col h-[320px] justify-between shadow-inner">
                    {/* Header */}
                    <div className="bg-[#075e54] text-white px-3 py-2 flex items-center gap-2">
                      <div className="h-6 w-6 rounded-full bg-emerald-800 flex items-center justify-center text-white font-bold text-[9px]">
                        TP
                      </div>
                      <div className="flex-1 truncate">
                        <div className="font-bold text-[11px] text-white flex items-center gap-1">
                          Torres del Parque PH
                          <span className="text-emerald-300 text-[10px]">✓</span>
                        </div>
                        <div className="text-[9px] text-emerald-100">Canal Oficial de Cartera</div>
                      </div>
                    </div>

                    {/* Mensaje */}
                    <div className="p-2.5 space-y-2 overflow-y-auto">
                      <div className="bg-white text-slate-800 p-2.5 rounded-xl rounded-tl-none shadow-xs border border-slate-200/80 space-y-1 text-[11px] leading-snug">
                        <div className="font-bold text-emerald-800">
                          Notificación de Cartera PH
                        </div>
                        <p className="text-[10px] text-slate-600">
                          Estimado propietario <strong>Apto 402</strong> (Juan Carlos Pérez):
                        </p>
                        <p className="text-[10px] text-slate-600">
                          Registra <strong>2 cuotas pendientes</strong> por valor de <strong>$1.150.000 COP</strong> (incluye intereses de mora certificados por la Superfinanciera Art. 30 Ley 675).
                        </p>
                        <div className="text-[9px] text-slate-400 text-right">09:15 AM ✓✓</div>
                      </div>

                      {/* Botón Interactivo PSE */}
                      {!hasPaidPse ? (
                        <div className="space-y-1.5 pt-1">
                          <button
                            onClick={handleSimulatePayment}
                            className="w-full py-2.5 px-3 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-[11px] shadow-sm flex items-center justify-center gap-1.5 transition-transform active:scale-95"
                          >
                            <CreditCard className="h-3.5 w-3.5" />
                            Pagar $1.150.000 con PSE
                          </button>
                          <div className="text-[9px] text-center text-slate-500">
                            🔒 Recaudo oficial PSE / Bancolombia
                          </div>
                        </div>
                      ) : (
                        <div className="p-2.5 rounded-lg bg-emerald-50 border border-emerald-200 text-center space-y-1 animate-fadeIn">
                          <div className="text-emerald-800 font-bold text-xs flex items-center justify-center gap-1">
                            <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600" />
                            PAGO APROBADO VÍA PSE
                          </div>
                          <div className="text-[10px] text-slate-600 font-mono">
                            Ref: WMP-984210 &bull; $1.150.000 COP
                          </div>
                          <div className="text-[9px] text-emerald-700 font-medium">
                            Paz y Salvo generado en la plataforma.
                          </div>
                        </div>
                      )}
                    </div>

                    <div className="p-1.5 bg-slate-200/80 text-center text-[9px] text-slate-500">
                      MiA Legal PH &bull; Notificación Certificada
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* PASO 4: TÍTULO EJECUTIVO EN 1 CLIC */}
        {currentStep === 4 && (
          <div className="space-y-5 animate-fadeIn">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
              <div>
                <Badge variant="outline" className="border-slate-300 text-slate-700 bg-slate-50 mb-1.5 text-xs font-normal">
                  Mérito Ejecutivo Pleno (Art. 48 Ley 675)
                </Badge>
                <h3 className="text-lg md:text-xl font-bold text-slate-900">
                  Emisión Inmediata de Título Ejecutivo para Embargo
                </h3>
              </div>
              <Button
                size="sm"
                className="bg-emerald-600 hover:bg-emerald-700 text-white font-medium gap-1.5 shadow-xs shrink-0"
                onClick={handleDownloadSampleTitle}
                disabled={isGeneratingPdf}
              >
                <Download className="h-3.5 w-3.5" />
                {isGeneratingPdf ? "Descargando..." : "Descargar Título en PDF"}
              </Button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-12 gap-5 items-center">
              {/* Previsualización Documental */}
              <div className="md:col-span-7 p-5 rounded-xl bg-white text-slate-900 border border-slate-200 shadow-sm space-y-2.5 font-serif">
                <div className="text-center border-b pb-2 border-slate-200">
                  <div className="text-[10px] font-bold tracking-wider text-slate-600">
                    REPÚBLICA DE COLOMBIA &bull; RÉGIMEN DE PROPIEDAD HORIZONTAL
                  </div>
                  <div className="text-xs font-bold text-slate-900 mt-0.5">
                    CERTIFICADO DE DEUDA CON MÉRITO EJECUTIVO (ART. 48 LEY 675 DE 2001)
                  </div>
                </div>

                <div className="text-xs space-y-1.5 text-slate-700 leading-relaxed font-sans">
                  <p>
                    <strong>Copropiedad:</strong> Conjunto Residencial Torres del Parque P.H. (NIT 900.542.118-4)
                  </p>
                  <p>
                    <strong>Inmueble / Unidad:</strong> Apto 701 - Torre 2 &bull; <strong>Propietario:</strong> Roberto Carlos Ruiz
                  </p>
                  <p className="text-[11px] text-slate-600">
                    El Administrador certifica la suma líquida, vencida y exigible:
                  </p>
                  <div className="p-2 rounded-lg bg-slate-50 border border-slate-200 font-mono text-center font-bold text-slate-900 text-xs">
                    TOTAL DEUDA EXIGIBLE: $3.850.000 COP
                  </div>
                  <p className="text-[10px] text-slate-500 italic">
                    Incluye capital e intereses de mora SFC. Listo para radicar demanda ejecutiva civil con solicitud de embargo de cuentas y apartamento.
                  </p>
                </div>

                <div className="pt-2 border-t border-slate-200 flex items-center justify-between text-xs font-sans">
                  <div className="text-[10px] text-slate-500">
                    Firma Electrónica Conforme Ley 527 de 1999
                  </div>
                  <Badge variant="outline" className="border-emerald-200 bg-emerald-50 text-emerald-800 text-[10px]">
                    Mérito Ejecutivo Válido
                  </Badge>
                </div>
              </div>

              {/* Explicación Legal */}
              <div className="md:col-span-5 space-y-3">
                <div className="p-4 rounded-xl bg-slate-50/60 border border-slate-200 space-y-2">
                  <div className="font-semibold text-xs text-slate-900">
                    Cero Semanas Liquidando Intereses Manuales
                  </div>
                  <p className="text-xs text-slate-600 leading-relaxed">
                    Ahorre semanas de trabajo manual calculando tasas mes a mes. El sistema emite el documento oficial que su abogado necesita para radicar la demanda de inmediato ante el juzgado civil.
                  </p>
                  <div className="space-y-1.5 text-xs text-slate-700">
                    <div className="flex items-center gap-1.5 text-emerald-800">
                      <Check className="h-3.5 w-3.5 text-emerald-600" />
                      <span>Liquidación SFC mes a mes desglosada</span>
                    </div>
                    <div className="flex items-center gap-1.5 text-emerald-800">
                      <Check className="h-3.5 w-3.5 text-emerald-600" />
                      <span>Firma del Administrador incorporada</span>
                    </div>
                    <div className="flex items-center gap-1.5 text-emerald-800">
                      <Check className="h-3.5 w-3.5 text-emerald-600" />
                      <span>Evita objeciones y excepciones en el juzgado</span>
                    </div>
                  </div>
                </div>

                <Button
                  size="sm"
                  className="w-full bg-slate-900 hover:bg-slate-800 text-white font-semibold text-xs gap-1.5 h-9"
                  asChild
                >
                  <a
                    href="https://wa.me/573507679981?text=Hola%2C%20deseo%20una%20prueba%20piloto%20de%20MiA%20Cartera%20PH%20para%20mi%20conjunto."
                    target="_blank"
                    rel="noreferrer"
                  >
                    Solicitar Prueba Piloto para su Conjunto
                  </a>
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* ── BARRA INFERIOR DE LOCUCIÓN (CLARA & SOBRIA) ── */}
        <div className="mt-6 pt-3 border-t border-slate-200 flex flex-col md:flex-row items-center justify-between gap-3 bg-slate-50 p-3.5 rounded-xl border border-slate-200/80">
          <div className="flex items-center gap-2.5">
            <div className="flex items-center justify-center h-7 w-7 rounded-lg bg-slate-200 text-slate-700 shrink-0">
              <Volume2 className="h-3.5 w-3.5" />
            </div>
            <div>
              <div className="text-[10px] font-bold text-slate-700 uppercase tracking-wider">
                Explicación Guiada (Capítulo {currentStep} de 4)
              </div>
              <p className="text-xs text-slate-600 mt-0.5 leading-relaxed italic">
                "{activeStep.voiceover}"
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2 shrink-0">
            <Button
              size="sm"
              variant="outline"
              className="border-slate-300 bg-white hover:bg-slate-100 text-slate-700 text-xs gap-1 h-7 font-medium shadow-xs"
              onClick={() => handleSelectStep(currentStep === 4 ? 1 : currentStep + 1)}
            >
              <span>{currentStep === 4 ? "Inicio" : "Siguiente"}</span>
              <ChevronRight className="h-3 w-3" />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
